// hazardous-chemicals/components/ChemicalForm.tsx
import React, { useState, useEffect } from 'react';
import { ChevronLeft, Upload, FileText, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { todayISO, fmtDate, dateOnly, isReviewDue } from '../utils/safety-utils';
import { Combobox } from '../../../../components/ui/combobox';
import api from '../../../../utils/api';
import type { User } from '../../../../types';
import { REVIEW_DUE_MONTHS } from '../../../../types/chemical.types';
import type { Chemical, GhsSignal, ChemicalLists, SdsFile } from '../../../../types/chemical.types';

const emptyChemical = (): Partial<Chemical> => ({
    name: '',
    quantity: '',
    ghsSignal: 'Warning',
    dangerousGoods: false,
    unNumber: '',
    poisonSchedule: '',
    storage: '',
    intendedUse: '',
    ppeRequired: '',
    location: '',
    owner: '',
    sds: null,
    lastReview: todayISO(),
    audit: [],
    deleted: false,
});

const CardBlock: React.FC<{ title: string; right?: React.ReactNode; children: React.ReactNode }> = ({ title, right, children }) => (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold">{title}</h3>
            {right}
        </div>
        {children}
    </div>
);

const Label: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
    <label className="text-sm font-medium">
        {children} {required && <span className="text-destructive">*</span>}
    </label>
);

const inputCls = 'w-full h-11 rounded-lg border border-border bg-card px-3 text-sm';
const areaCls = 'w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm';

export const ChemicalForm: React.FC<{
    initial?: Chemical | null;
    lists: ChemicalLists;
    onCancel: () => void;
    onSave: (c: Partial<Chemical>, sdsFile?: File) => void;
    onUploadSds?: (chemicalId: string, file: File) => Promise<void>;
}> = ({ initial, lists, onCancel, onSave, onUploadSds }) => {
    const isEdit = !!initial;
    const [c, setC] = useState<Partial<Chemical>>(() => {
        if (initial) return { ...initial };
        return emptyChemical();
    });
    const [ppeSet, setPpeSet] = useState<Set<string>>(
        () => new Set((initial?.ppeRequired ?? '').split(',').map((s) => s.trim()).filter(Boolean)),
    );
    const [users, setUsers] = useState<User[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [pendingSdsFile, setPendingSdsFile] = useState<File | null>(null);
    const [uploadingSds, setUploadingSds] = useState(false);
    const [localSds, setLocalSds] = useState<SdsFile | null>(initial?.sds ?? null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const set = (patch: Partial<Chemical>) => setC((prev) => ({ ...prev, ...patch }));

    useEffect(() => {
        const fetchUsers = async () => {
            setUsersLoading(true);
            try {
                const res = await api.get<{ users: User[] }>('/api/users?archived=false&all_users=true');
                setUsers(res.users || []);
            } catch {
                // silently fail — owner field falls back to free text
            } finally {
                setUsersLoading(false);
            }
        };
        fetchUsers();
    }, []);

    const togglePpe = (item: string) => {
        setPpeSet((prev) => {
            const next = new Set(prev);
            next.has(item) ? next.delete(item) : next.add(item);
            return next;
        });
    };

    // Next review falls due REVIEW_DUE_MONTHS after the last review date.
    const nextReviewDate = (() => {
        const day = dateOnly(c.lastReview);
        if (!day) return '';
        const dt = new Date(`${day}T00:00:00`);
        if (isNaN(dt.getTime())) return '';
        dt.setMonth(dt.getMonth() + REVIEW_DUE_MONTHS);
        // Format from local components — toISOString() can shift a day in UTC+ timezones
        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    })();

    const submit = () => {
        if (!c.name?.trim()) {
            toast.error('Chemical name is required');
            return;
        }
        if (!c.ghsSignal) {
            toast.error('GHS signal word is required');
            return;
        }
        // Send null when cleared — the DB column is a nullable date and '' is invalid
        onSave({ ...c, lastReview: dateOnly(c.lastReview) || null, ppeRequired: Array.from(ppeSet).join(', '), sds: localSds }, pendingSdsFile || undefined);
    };

    const SaveButton = (
        <button
            onClick={submit}
            className="h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
        >
            {isEdit ? 'Save Changes' : 'Save Chemical'}
        </button>
    );

    return (
        <div className="bg-background min-h-screen pb-10 px-0 sm:px-4 lg:px-0">
            <div className="max-w-3xl mx-auto px-0 sm:px-4 lg:px-0 py-7">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <button onClick={onCancel} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-2">
                            <ChevronLeft size={16} />
                            Chemical Register
                        </button>
                        <h1 className="text-2xl font-medium">{isEdit ? 'Edit Hazardous Chemical' : 'Add Hazardous Chemical'}</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            {isEdit ? 'Update the record and save your changes.' : 'Complete the form below and save to register the substance.'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={onCancel} className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                            Cancel
                        </button>
                        {SaveButton}
                    </div>
                </div>

                <CardBlock title="Chemical Details">
                    <div className="space-y-4">
                        <div>
                            <Label required>Chemical Name</Label>
                            <input value={c.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Hydrochloric Acid" className={`${inputCls} mt-1.5`} />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Label>Quantity</Label>
                                <input value={c.quantity} onChange={(e) => set({ quantity: e.target.value })} placeholder="e.g. 20 L" className={`${inputCls} mt-1.5`} />
                            </div>
                            <div>
                                <Label>UN Number</Label>
                                <input value={c.unNumber} onChange={(e) => set({ unNumber: e.target.value })} placeholder="e.g. UN1789" className={`${inputCls} mt-1.5`} />
                            </div>
                            <div>
                                <Label>Poison Schedule / pH</Label>
                                <input value={c.poisonSchedule} onChange={(e) => set({ poisonSchedule: e.target.value })} placeholder="e.g. S7 / pH 1" className={`${inputCls} mt-1.5`} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Location</Label>
                                <input value={c.location} onChange={(e) => set({ location: e.target.value })} className={`${inputCls} mt-1.5`} />
                            </div>
                            <div>
                                <Label>Owner / Responsible Person</Label>
                                {usersLoading ? (
                                    <div className={`${inputCls} mt-1.5 flex items-center text-muted-foreground`}>
                                        Loading users...
                                    </div>
                                ) : users.length > 0 ? (
                                    <div className="mt-1.5">
                                        <Combobox
                                            options={users.map((u) => ({
                                                label: u.name || u.email || `User ${u.id}`,
                                                value: u.name || u.email || String(u.id),
                                                subtitle: u.jobRole || (typeof u.department === 'string' ? u.department : u.department?.departmentName) || u.email,
                                            }))}
                                            value={c.owner || undefined}
                                            onChange={(value) => set({ owner: value || '' })}
                                            placeholder="Select owner..."
                                            searchPlaceholder="Search users..."
                                            emptyMessage="No users found."
                                            className="w-full"
                                        />
                                    </div>
                                ) : (
                                    <input
                                        value={c.owner}
                                        onChange={(e) => set({ owner: e.target.value })}
                                        placeholder="e.g. Sarah Mitchell"
                                        className={`${inputCls} mt-1.5`}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Last Review Date</Label>
                                <input
                                    type="date"
                                    value={dateOnly(c.lastReview)}
                                    onChange={(e) => set({ lastReview: e.target.value || null })}
                                    className={`${inputCls} mt-1.5`}
                                />
                                {isReviewDue(c.lastReview) ? (
                                    <p className="text-xs font-medium text-amber-600 mt-1.5">
                                        {dateOnly(c.lastReview)
                                            ? 'Review is due — set this to the date the chemical was last reviewed.'
                                            : 'No review recorded — this chemical is flagged as Review Due.'}
                                    </p>
                                ) : (
                                    <p className="text-xs text-muted-foreground mt-1.5">
                                        Next review due {fmtDate(nextReviewDate)}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </CardBlock>

                <CardBlock title="GHS Classification">
                    <Label required>GHS Signal Word</Label>
                    <div className="flex gap-2 mt-1.5 mb-4">
                        {(['Danger', 'Warning', 'None'] as GhsSignal[]).map((g) => {
                            const active = c.ghsSignal === g;
                            const activeCls =
                                g === 'Danger'
                                    ? 'bg-destructive text-destructive-foreground border-destructive'
                                    : g === 'Warning'
                                    ? 'bg-amber-500 text-white border-amber-500'
                                    : 'bg-primary text-primary-foreground border-primary';
                            return (
                                <button
                                    key={g}
                                    onClick={() => set({ ghsSignal: g })}
                                    className={`h-10 px-6 rounded-lg text-sm font-medium border ${active ? activeCls : 'border-border bg-card hover:bg-accent'}`}
                                >
                                    {g}
                                </button>
                            );
                        })}
                    </div>
                    <button
                        onClick={() => set({ dangerousGoods: !c.dangerousGoods })}
                        className="flex items-center gap-2.5"
                        type="button"
                    >
                        <span className={`h-6 w-10 rounded-full transition-colors relative ${c.dangerousGoods ? 'bg-primary' : 'bg-muted'}`}>
                            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${c.dangerousGoods ? 'left-[18px]' : 'left-0.5'}`} />
                        </span>
                        <span className="text-sm">Classified as Dangerous Goods</span>
                    </button>
                </CardBlock>

                <CardBlock title="Storage &amp; Use">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <Label>Storage Requirements</Label>
                            <textarea rows={3} value={c.storage} onChange={(e) => set({ storage: e.target.value })} placeholder="e.g. Cool, dry, ventilated. Keep away from acids." className={`${areaCls} mt-1.5`} />
                        </div>
                        <div>
                            <Label>Intended Use</Label>
                            <textarea rows={3} value={c.intendedUse} onChange={(e) => set({ intendedUse: e.target.value })} placeholder="e.g. Cleaning &amp; descaling of equipment" className={`${areaCls} mt-1.5`} />
                        </div>
                    </div>
                    <div>
                        <Label>Notes</Label>
                        <textarea rows={2} placeholder="Additional handling or disposal notes..." className={`${areaCls} mt-1.5`} />
                    </div>
                </CardBlock>

                <CardBlock title="Required PPE">
                    <div className="grid grid-cols-3 gap-2">
                        {lists.ppeOptions.map((item) => (
                            <button
                                key={item}
                                type="button"
                                onClick={() => togglePpe(item)}
                                className={`flex items-center gap-2 border rounded-lg px-3 py-2.5 text-sm text-left ${
                                    ppeSet.has(item) ? 'border-primary bg-accent' : 'border-border hover:bg-accent'
                                }`}
                            >
                                <span className={`h-4 w-4 rounded-sm border flex items-center justify-center ${ppeSet.has(item) ? 'bg-primary border-primary' : 'border-border'}`}>
                                    {ppeSet.has(item) && <span className="h-1.5 w-1.5 bg-primary-foreground rounded-[1px]" />}
                                </span>
                                {item}
                            </button>
                        ))}
                    </div>
                </CardBlock>

                <CardBlock
                    title="Safety Data Sheet"
                    right={<span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-md">Required by WHS regulations</span>}
                >
                    {isEdit && localSds ? (
                        <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card mb-3">
                            <FileText size={20} className="text-emerald-600" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{localSds.fileName}</div>
                                <div className="text-xs text-muted-foreground">
                                    Uploaded {fmtDate(localSds.uploadedAt)} · {localSds.sizeLabel}
                                </div>
                            </div>
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingSds}
                                className="h-8 px-3 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                            >
                                {uploadingSds ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                                Replace
                            </button>
                        </div>
                    ) : pendingSdsFile ? (
                        <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card mb-3">
                            <FileText size={20} className="text-emerald-600" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{pendingSdsFile.name}</div>
                                <div className="text-xs text-muted-foreground">
                                    {(pendingSdsFile.size / (1024 * 1024)).toFixed(1)} MB · PDF
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setPendingSdsFile(null);
                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                }}
                                className="h-8 w-8 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors inline-flex items-center justify-center"
                                title="Remove"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ) : null}

                    {(!isEdit || !localSds) && !pendingSdsFile && (
                        <div
                            onClick={() => !uploadingSds && fileInputRef.current?.click()}
                            className="border border-dashed border-border rounded-lg py-8 flex flex-col items-center text-center bg-muted/20 cursor-pointer hover:bg-muted/40"
                        >
                            <Upload size={20} className="text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">
                                Drop SDS here or <span className="text-primary font-medium">browse</span>
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">PDF only, up to 20MB</p>
                        </div>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        className="hidden"
                        onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;

                            if (!file.type.includes('pdf')) {
                                toast.error('SDS files must be in PDF format');
                                if (fileInputRef.current) fileInputRef.current.value = '';
                                return;
                            }

                            const maxSize = 20 * 1024 * 1024;
                            if (file.size > maxSize) {
                                toast.error('File size too large. Maximum size is 20MB.');
                                if (fileInputRef.current) fileInputRef.current.value = '';
                                return;
                            }

                            if (isEdit && initial && onUploadSds) {
                                setUploadingSds(true);
                                try {
                                    await onUploadSds(initial.id, file);
                                    // Build a local SDS preview from the file since the parent
                                    // will update its state but we need immediate feedback here
                                    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
                                    setLocalSds({
                                        fileName: file.name,
                                        uploadedAt: new Date().toISOString(),
                                        sizeLabel: `${sizeMB} MB`,
                                    });
                                    toast.success('SDS uploaded successfully');
                                } catch (err: any) {
                                    toast.error(err.message || 'Failed to upload SDS');
                                } finally {
                                    setUploadingSds(false);
                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                }
                            } else {
                                setPendingSdsFile(file);
                            }
                        }}
                    />
                </CardBlock>

                <div className="flex justify-end gap-2">
                    <button onClick={onCancel} className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                        Cancel
                    </button>
                    {SaveButton}
                </div>
            </div>
        </div>
    );
};
